import { Injectable, signal } from '@angular/core';

export type ToastTone = 'error' | 'success' | 'info';

export interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
}

export interface ToastOptions {
  message: string;
  tone?: ToastTone;
  durationMs?: number;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  readonly items = signal<ToastItem[]>([]);

  private nextId = 1;
  private readonly timeoutHandles = new Map<number, number>();

  show(options: ToastOptions): number {
    const id = this.nextId++;
    const toast: ToastItem = {
      id,
      message: options.message,
      tone: options.tone ?? 'info'
    };

    this.items.update((items) => [...items, toast]);

    const timeoutHandle = window.setTimeout(() => {
      this.dismiss(id);
    }, options.durationMs ?? 6000);

    this.timeoutHandles.set(id, timeoutHandle);
    return id;
  }

  error(message: string, durationMs?: number): number {
    return this.show({
      message,
      tone: 'error',
      durationMs
    });
  }

  dismiss(id: number): void {
    const timeoutHandle = this.timeoutHandles.get(id);

    if (timeoutHandle !== undefined) {
      window.clearTimeout(timeoutHandle);
      this.timeoutHandles.delete(id);
    }

    this.items.update((items) => items.filter((item) => item.id !== id));
  }

  clear(): void {
    for (const timeoutHandle of this.timeoutHandles.values()) {
      window.clearTimeout(timeoutHandle);
    }

    this.timeoutHandles.clear();
    this.items.set([]);
  }
}
